import type {
  Artifact,
  ArtifactDeltaEvent,
  ArtifactEndEvent,
  ArtifactErrorEvent,
  ArtifactListItem,
  ArtifactStartEvent,
  ArtifactUiPhase,
} from "../types/artifact";

export interface ArtifactStoreSnapshot {
  artifacts: Map<string, Artifact>;
  activeArtifactId: string | null;
  panelOpen: boolean;
  /** Unified phase for document-generation UX (replaces a separate boolean). */
  artifactUiPhase: ArtifactUiPhase;
  /** All artifact IDs grouped by identifier, ordered oldest→newest */
  versionsByIdentifier: Map<string, string[]>;
}

class ArtifactStore {
  private artifacts: Map<string, Artifact> = new Map();
  private activeArtifactId: string | null = null;
  private panelOpen = false;
  private artifactUiPhase: ArtifactUiPhase = "idle";
  private versionsByIdentifier: Map<string, string[]> = new Map();
  /** Resolve identifier → artifact id for setActiveArtifact when only identifier is known (e.g. from message-stream marker). */
  private identifierToId: Map<string, string> = new Map();
  private listeners: Set<() => void> = new Set();
  private snapshotCache: ArtifactStoreSnapshot | null = null;

  onArtifactStart(event: ArtifactStartEvent): void {
    const artifact: Artifact = {
      id: event.id,
      identifier: event.identifier,
      title: event.title,
      type: event.type,
      language: event.language,
      content: "",
      version: 0,
      status: "streaming",
    };

    this.artifacts.set(event.id, artifact);
    this.trackVersion(event.identifier, event.id);
    this.activeArtifactId = event.id;
    // Keep the side panel closed until streaming completes so chat shows
    // "Generating document…" instead of a stale open document.
    this.panelOpen = false;
    this.invalidate();
  }

  onArtifactDelta(event: ArtifactDeltaEvent): void {
    const artifact = this.artifacts.get(event.id);
    if (!artifact) return;

    artifact.content += event.contentDelta;
    this.invalidate();
  }

  onArtifactEnd(event: ArtifactEndEvent): void {
    const artifact = this.artifacts.get(event.id);
    if (!artifact) return;

    artifact.status = "complete";
    artifact.version = event.version;
    artifact.identifier = event.identifier;
    this.activeArtifactId = event.id;
    this.panelOpen = true;
    this.artifactUiPhase = "idle";
    this.invalidate();
  }

  onArtifactError(event: ArtifactErrorEvent): void {
    const artifact = this.artifacts.get(event.id);
    if (!artifact) return;

    artifact.status = "error";
    artifact.error = event.error;
    this.activeArtifactId = event.id;
    this.panelOpen = true;
    this.artifactUiPhase = "idle";
    this.invalidate();
  }

  setActiveArtifact(
    artifactIdOrIdentifier: string,
    options?: { openPanel?: boolean },
  ): void {
    const openPanel = options?.openPanel !== false;
    if (this.artifacts.has(artifactIdOrIdentifier)) {
      this.activeArtifactId = artifactIdOrIdentifier;
    } else if (this.identifierToId.has(artifactIdOrIdentifier)) {
      this.activeArtifactId =
        this.identifierToId.get(artifactIdOrIdentifier) ??
        artifactIdOrIdentifier;
    } else {
      this.activeArtifactId = artifactIdOrIdentifier;
    }
    if (openPanel) {
      this.panelOpen = true;
    }
    this.invalidate();
  }

  closePanel(): void {
    this.panelOpen = false;
    this.activeArtifactId = null;
    this.artifactUiPhase = "idle";
    this.invalidate();
  }

  /**
   * Called when create/update_artifact is detected: show inline "generating"
   * state in chat and keep the artifact panel closed until the artifact is ready.
   */
  beginDocumentGeneration(): void {
    this.artifactUiPhase = "document_generating";
    this.panelOpen = false;
    this.invalidate();
  }

  endDocumentGeneration(): void {
    if (this.artifactUiPhase === "idle") return;
    this.artifactUiPhase = "idle";
    this.invalidate();
  }

  /** @deprecated Use beginDocumentGeneration */
  openPendingPanel(): void {
    this.beginDocumentGeneration();
  }

  /**
   * Populate the store from a REST API response (e.g. on chat open).
   * Existing streaming artifacts are preserved.
   */
  loadArtifacts(items: ArtifactListItem[]): void {
    for (const item of items) {
      this.identifierToId.set(item.identifier, item.id);
      if (this.artifacts.has(item.id)) {
        const existing = this.artifacts.get(item.id)!;
        if (!existing.chatId && item.chatId) {
          existing.chatId = item.chatId;
        }
        // The REST response confirms the artifact is complete on the server.
        // Promote streaming artifacts so ArtifactPanel can fetch content and
        // show download/save actions.
        if (existing.status === "streaming") {
          existing.status = "complete";
          existing.version = item.version;
        }
        continue;
      }

      const artifact: Artifact = {
        id: item.id,
        identifier: item.identifier,
        title: item.title,
        type: item.type,
        language: item.language,
        content: "",
        version: item.version,
        status: "complete",
        chatId: item.chatId,
        messageId: item.messageId,
        createdAt: item.createdAt,
      };

      this.artifacts.set(item.id, artifact);
      this.trackVersion(item.identifier, item.id);
    }
    this.invalidate();
  }

  /**
   * Update version numbers from a REST refresh (after stream completes).
   */
  refreshVersions(items: ArtifactListItem[]): void {
    for (const item of items) {
      this.identifierToId.set(item.identifier, item.id);
      const artifact = this.artifacts.get(item.id);
      if (artifact) {
        artifact.version = item.version;
      }
    }
    this.invalidate();
  }

  /**
   * Set content for an existing artifact (e.g. after fetching via getArtifact REST).
   */
  setArtifactContent(id: string, content: string): void {
    const artifact = this.artifacts.get(id);
    if (artifact) {
      artifact.content = content;
      this.invalidate();
    }
  }

  getArtifact(idOrIdentifier: string): Artifact | undefined {
    if (this.artifacts.has(idOrIdentifier)) {
      return this.artifacts.get(idOrIdentifier);
    }
    // Resolve via identifier→id (needed when card uses the streamed identifier
    // but the artifact was loaded from the REST API under its UUID).
    const resolved = this.identifierToId.get(idOrIdentifier);
    if (resolved) {
      return this.artifacts.get(resolved);
    }
    return undefined;
  }

  getActiveArtifact(): Artifact | undefined {
    if (!this.activeArtifactId) return undefined;
    const direct = this.artifacts.get(this.activeArtifactId);
    if (direct) return direct;
    const resolved = this.identifierToId.get(this.activeArtifactId);
    if (resolved) return this.artifacts.get(resolved);
    return undefined;
  }

  getVersionsForIdentifier(identifier: string): Artifact[] {
    const ids = this.versionsByIdentifier.get(identifier) ?? [];
    return ids
      .map((id) => this.artifacts.get(id))
      .filter((a): a is Artifact => a !== undefined);
  }

  clearArtifacts(): void {
    this.artifacts.clear();
    this.versionsByIdentifier.clear();
    this.identifierToId.clear();
    this.activeArtifactId = null;
    this.panelOpen = false;
    this.artifactUiPhase = "idle";
    this.invalidate();
  }

  /**
   * Check if any artifacts were created during the current stream.
   */
  hasStreamingArtifacts(): boolean {
    for (const artifact of this.artifacts.values()) {
      if (artifact.status === "streaming" || artifact.status === "complete") {
        return true;
      }
    }
    return false;
  }

  // -- useSyncExternalStore contract --

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getSnapshot(): ArtifactStoreSnapshot {
    if (!this.snapshotCache) {
      this.snapshotCache = {
        artifacts: this.artifacts,
        activeArtifactId: this.activeArtifactId,
        panelOpen: this.panelOpen,
        artifactUiPhase: this.artifactUiPhase,
        versionsByIdentifier: this.versionsByIdentifier,
      };
    }
    return this.snapshotCache;
  }

  // -- internals --

  private trackVersion(identifier: string, id: string): void {
    const versions = this.versionsByIdentifier.get(identifier) ?? [];
    if (!versions.includes(id)) {
      versions.push(id);
      this.versionsByIdentifier.set(identifier, versions);
    }
  }

  private invalidate(): void {
    this.snapshotCache = null;
    for (const listener of this.listeners) {
      listener();
    }
  }
}

export const artifactStore = new ArtifactStore();
